import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Skeleton,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { monitoramentoService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import DateRangeSelector from '../../components/monitoramento/DateRangeSelector';
import KpiCard from '../../components/monitoramento/KpiCard';
import { ChatIAButton } from '../../components/ChatIA';
import AnalisePredicaoCard from '../../components/saude/AnalisePredicaoCard';
import HotelIcon from '@mui/icons-material/Hotel';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BusinessIcon from '@mui/icons-material/Business';

const toISO = (d: Date): string => d.toISOString().slice(0, 10);

/**
 * Retorna cor de fundo baseada na taxa de ocupacao:
 * - verde: < 70%
 * - amarelo: 70-85%
 * - vermelho: > 85%
 */
const getOcupacaoColor = (taxa: number): string => {
  if (taxa > 85) return 'rgba(208, 30, 41, 0.15)';
  if (taxa >= 70) return 'rgba(231, 111, 0, 0.15)';
  return 'rgba(33, 123, 0, 0.15)';
};

const getOcupacaoTextColor = (taxa: number): string => {
  if (taxa > 85) return '#D01E29';
  if (taxa >= 70) return '#E76F00';
  return '#217B00';
};

const MonitoramentoLeitos: React.FC = () => {
  const { municipioAtivo } = useAuth();

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const hoje = new Date();
  const tresMesesAtras = new Date();
  tresMesesAtras.setMonth(tresMesesAtras.getMonth() - 3);
  const [dataInicio, setDataInicio] = useState(toISO(tresMesesAtras));
  const [dataFim, setDataFim] = useState(toISO(hoje));

  const handleDateChange = (di: string, df: string) => {
    setDataInicio(di);
    setDataFim(df);
  };

  useEffect(() => {
    if (!municipioAtivo?.id) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const result = await monitoramentoService.getLeitos(
          municipioAtivo.id,
          dataInicio,
          dataFim,
        );

        setData(result);
      } catch (err: any) {
        setError(
          err?.response?.data?.message || 'Erro ao carregar dados de leitos.'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [municipioAtivo?.id, dataInicio, dataFim]);

  if (!municipioAtivo) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', px: 3 }}>
        <Typography variant="h5" color="text.secondary" gutterBottom>
          Nenhum municipio selecionado
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Selecione um municipio para visualizar dados de leitos.
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
        <Skeleton variant="text" width="30%" height={48} />
        <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 2, mt: 2, mb: 3 }} />
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} sm={4} key={i}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2, mb: 3 }} />
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
      </Box>
    );
  }

  const kpis = data?.kpis || {};
  const tendencia = data?.tendencia || [];
  const heatmap = data?.heatmap || [];

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Leitos Hospitalares
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
            {municipioAtivo.nome}
          </Typography>
        </Box>
        <DateRangeSelector
          dataInicio={dataInicio}
          dataFim={dataFim}
          onChange={handleDateChange}
        />
      </Box>

      {/* KPIs */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <KpiCard
            titulo="Taxa Media de Ocupacao"
            valor={`${(kpis.taxa_media ?? 0).toLocaleString('pt-BR')}%`}
            icone={<HotelIcon />}
            cor="#007AF0"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <KpiCard
            titulo="Dias Criticos"
            valor={(kpis.dias_criticos ?? 0).toLocaleString('pt-BR')}
            icone={<WarningAmberIcon />}
            cor="#D01E29"
            subtitulo="Ocupacao acima de 85%"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <KpiCard
            titulo="Unidades com Leitos"
            valor={(kpis.unidades_com_leitos ?? 0).toLocaleString('pt-BR')}
            icone={<BusinessIcon />}
            cor="#02786D"
          />
        </Grid>
      </Grid>

      {/* Tendencia - LineChart */}
      <Paper elevation={1} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Tendencia da Taxa de Ocupacao
        </Typography>
        {tendencia.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={tendencia} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="data" tick={{ fontSize: 12 }} />
              <YAxis
                domain={[0, 100]}
                tickFormatter={(v: number) => `${v}%`}
              />
              <Tooltip
                formatter={(value: any) => [
                  `${Number(value).toLocaleString('pt-BR')}%`,
                  'Taxa Media',
                ]}
              />
              <Line
                type="monotone"
                dataKey="taxa_media"
                stroke="#007AF0"
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            Sem dados de tendencia para o periodo selecionado.
          </Typography>
        )}
      </Paper>

      {/* Heatmap - Tabela com cores */}
      <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Ocupacao por Unidade
        </Typography>
        {heatmap.length > 0 ? (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Unidade</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>
                    Taxa de Ocupacao
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {heatmap.map((row: any, index: number) => (
                  <TableRow key={index}>
                    <TableCell>{row.nome_unidade}</TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        backgroundColor: getOcupacaoColor(row.taxa_ocupacao ?? 0),
                        color: getOcupacaoTextColor(row.taxa_ocupacao ?? 0),
                        fontWeight: 700,
                      }}
                    >
                      {(row.taxa_ocupacao ?? 0).toLocaleString('pt-BR')}%
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            Sem dados de ocupacao por unidade.
          </Typography>
        )}
      </Paper>

      {municipioAtivo && data && (
        <AnalisePredicaoCard
          dadosContexto={JSON.stringify(data)}
          municipioId={municipioAtivo.id}
          painelId="leitos"
          contexto="dashboard"
          manualTrigger
        />
      )}

      <ChatIAButton
        contexto="dashboard"
        dadosContexto={JSON.stringify(data)}
        fabSx={{ bottom: 24, right: 24, top: 'auto' }}
      />
    </Box>
  );
};

export default MonitoramentoLeitos;
